import * as UIElements from '@leverege/ui-elements'
import FirebaseSync from './FirebaseSync'
import ComponentTheme from './ComponentTheme'
import Rollover from './Rollover'

const EMPTY = {}

const uiElementsValues = Object.values( UIElements )
export default class ActiveTheme {
  
  /**
   * Create an ActiveTheme
   * @param {FirebaseSync} sync
   **/
  constructor( options ) {
    const { altTheme, appearanceProjectId, appearanceId, addWindowCommands, ...rest } = options
    this.sync = new FirebaseSync( { ...rest, onChange : this.onSyncChange } )
    this.sync.start( { appearanceProjectId, appearanceId } )
    this.altTheme = altTheme
    this.rendered = false
    if ( addWindowCommands !== false ) {
      this.addWindowCommands()
    }
    this.componentTheme = new ComponentTheme( { }, altTheme )
  }

  onSyncChange = ( sync ) => {
    this.component = sync.component
    this.componentTheme = new ComponentTheme( sync, this.altTheme )
    if ( this.onChange ) { 
      this.onChange( this.componentTheme )
    }
  }

  setOnChange( cb ) {
    this.onChange = cb
    if ( cb && this.componentTheme ) {
      // Let the out party know we have 'new' data
      setTimeout( ( ) => { 
        this.onChange( this.componentTheme ) 
      }, 1 )
    }
  }

  get currentTheme() {
    return this.componentTheme
  }

  addWindowCommands( ) {
    this.rollover = new Rollover()
    document.addEventListener( 'keypress', this.onKeyPressed )
    document.addEventListener( 'mousemove', this.onMouseMove )

  }

  onKeyPressed = ( evt ) => {
    if ( evt.key === 'p' && evt.ctrlKey === true ) {
      this.requestProjectId( )
    }
    if ( evt.key === 'i' && evt.ctrlKey === true ) {
      this.rollover.toggleActive( )
    }

  }

  onMouseMove = ( evt ) => {
    if ( !this.rollover.active && !evt.ctrlKey ) {
      if ( this.rendered ) {
        this.rollover.setHits( [], evt )
        this.rendered = false
      }
      return
    }
    this.rendered = true
    let el = evt.target;
    const hits = []
    while ( el ) {
      const tv = this.getVariant( el, hits.length > 0 ? hits[hits.length - 1].debugOwner : null )
      if ( tv ) {
        hits.push( tv )
        // console.log( 'Type:', tv.type, 'Variant', tv.variant, el )
      }
      el = el.parentNode
    }
    this.rollover.setHits( hits, evt )
  }

  resolveVariant( componentType, variant ) {
    if ( this.sync.active === false ) {
      return this.altTheme.classes( componentType, variant )
    }
    return this.componentTheme.classes( componentType, variant )
  }

  getVariant( el, ignoreComponent ) {
    // eslint-disable-next-line
    for ( const key in el ) {
      if ( key.startsWith( '__reactFiber$' ) ) {
        const fiberNode = el[key];
        let component = fiberNode && fiberNode._debugOwner;
        if ( component == null ) {
          return null
        }
        let depth = 0 // prevent infinite
        do {
          const context = component?.stateNode?.context
          if ( ( context && context instanceof ComponentTheme ) || uiElementsValues.includes( component.type ) ) {
            if ( ignoreComponent === component ) {
              return null
            }
            return { 
              type : component.type.displayName || component.type.name,
              variant : component?.stateNode?.props?.variant || component?.pendingProps?.variant || 'default',
              layout : component?.stateNode?.props?.layout || component?.pendingProps?.layout || null,
              debugOwner : component
            }
          }
          depth++
          component = component._debugOwner
        } while ( depth < 100 && component )
        return null
      }
    }
    return null
  }

  getVariant2( el ) {
    // eslint-disable-next-line
    for ( const key in el ) {
      if ( key.startsWith( '__reactFiber$' ) ) {
        const fiberNode = el[key];
        const component = fiberNode && fiberNode._debugOwner;
        const context = component?.stateNode?.context
        const variant = component?.stateNode?.props?.variant

        if ( context && context instanceof ComponentTheme ) {
          return { 
            type : component.type.displayName || component.type.name,
            variant : variant || 'default'
          }
        }
        return null
      }
    }
    return null
  }

  requestProjectId( ) {
    // eslint-disable-next-line
    const id = prompt( 'Enter the Project Id to Attach to', '' )
    if ( id != null ) {
      this.sync.start( { appearanceProjectId : id } )
    }
  }

  classes( component, type, variant, ...rest ) {
    if ( this.sync.active === false ) {
      return this.altTheme.classes( component, type, variant, ...rest )
    }
    return this.componentTheme.classes( component, type, variant, ...rest )
  }

  subclasses( component, type, ...rest ) {
    if ( this.sync.active === false ) {
      return this.altTheme.subclasses( component, type, ...rest )
    }
    return this.componentTheme.classes( component, type, ...rest )
  }

  /**
   * Returns any default props for the supplied Component and Variant.
   * If the third argument is an object, it will be overlaid on top of
   * the component's default props and the component's variant props.
   *
   * If the second argument is an object, it is considered to be the
   * React component's actual props object, and the variant is looked
   * up from it. This means from render, you can invoke:
   *   const { markClass } = theme.props( 'Checkbox', this.props )
   * @param {string} component the name of the component type. eg "Button"
   * @param {string|object} variant the variant name to requsest. eg "small"
   * @return {object} an object containing the default props. This will be a 
   * merger of the default variant's props and the specified variants props. 
   * This will always return an object
   **/
  props( component, variant, userProps = EMPTY ) {
    if ( this.sync.active === false ) {
      return this.altTheme.props( component, userProps )
    }
    return this.componentTheme.props( component, variant, userProps )
  }

  resources( type, name ) {
    if ( this.sync.active === false ) {
      return this.altTheme.resources( type, name )
    }
    return this.componentTheme.resources( type, name )
  }
}
